/**
 * Explain Plan Modal Component
 *
 * Displays PostgreSQL EXPLAIN ANALYZE output for a query.
 * Provides detailed query execution plan analysis.
 *
 * Features:
 * - Toggle between EXPLAIN and EXPLAIN ANALYZE
 * - Formatted plan output with syntax highlighting
 * - Execution metrics (timing, rows, cost)
 * - Copy to clipboard functionality
 * - Uses Shadcn/ui Dialog component
 * - WCAG 2.1 Level AA accessible
 */

import { useState } from 'react';
import { useExplainAnalyze } from '../../../hooks/admin';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertCircle,
  Copy,
  Check,
  Play,
  FileText,
  Clock,
  Rows3,
  Calculator,
} from 'lucide-react';

export interface ExplainPlanModalProps {
  /** The SQL query to analyze */
  query: string;
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal open state changes */
  onOpenChange: (open: boolean) => void;
}

/**
 * Format bytes to human-readable size
 */
function formatMs(ms: number): string {
  if (ms < 1) return `${(ms * 1000).toFixed(2)} μs`;
  if (ms < 1000) return `${ms.toFixed(2)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

/**
 * Recursively format the EXPLAIN plan for display
 */
function formatPlanNode(node: any, indent: number = 0): string {
  if (!node) return '';

  const prefix = '  '.repeat(indent);
  let result = '';

  // Node type and basic info
  if (node['Node Type']) {
    result += `${prefix}→ ${node['Node Type']}`;

    if (node['Relation Name']) {
      result += ` on ${node['Relation Name']}`;
    }

    if (node['Index Name']) {
      result += ` using ${node['Index Name']}`;
    }

    if (node['Alias']) {
      result += ` (${node['Alias']})`;
    }

    result += '\n';

    // Cost and timing
    if (node['Total Cost'] !== undefined) {
      result += `${prefix}   Cost: ${node['Startup Cost']?.toFixed(2) ?? '?'}..${node['Total Cost']?.toFixed(2) ?? '?'}`;
      if (node['Actual Total Time'] !== undefined) {
        result += ` | Actual: ${formatMs(node['Actual Startup Time'] ?? 0)}..${formatMs(node['Actual Total Time'])}`;
      }
      result += '\n';
    }

    // Rows
    if (node['Plan Rows'] !== undefined) {
      result += `${prefix}   Rows: ${node['Plan Rows']} estimated`;
      if (node['Actual Rows'] !== undefined) {
        result += ` | ${node['Actual Rows']} actual`;
        if (node['Actual Loops'] && node['Actual Loops'] > 1) {
          result += ` × ${node['Actual Loops']} loops`;
        }
      }
      result += '\n';
    }

    // Filter/conditions
    if (node['Filter']) {
      result += `${prefix}   Filter: ${node['Filter']}\n`;
    }
    if (node['Index Cond']) {
      result += `${prefix}   Index Cond: ${node['Index Cond']}\n`;
    }
    if (node['Hash Cond']) {
      result += `${prefix}   Hash Cond: ${node['Hash Cond']}\n`;
    }
    if (node['Join Filter']) {
      result += `${prefix}   Join Filter: ${node['Join Filter']}\n`;
    }

    // Rows removed by filter
    if (node['Rows Removed by Filter']) {
      result += `${prefix}   Rows Removed by Filter: ${node['Rows Removed by Filter']}\n`;
    }

    // Sort/hash info
    if (node['Sort Key']) {
      result += `${prefix}   Sort Key: ${Array.isArray(node['Sort Key']) ? node['Sort Key'].join(', ') : node['Sort Key']}\n`;
    }
    if (node['Sort Method']) {
      result += `${prefix}   Sort Method: ${node['Sort Method']}`;
      if (node['Sort Space Used']) {
        result += ` using ${node['Sort Space Used']} kB`;
      }
      result += '\n';
    }
  }

  // Process child plans
  if (node['Plans']) {
    for (const child of node['Plans']) {
      result += formatPlanNode(child, indent + 1);
    }
  }

  return result;
}

/**
 * ExplainPlanModal - Main component
 */
export function ExplainPlanModal({ query, open, onOpenChange }: ExplainPlanModalProps) {
  const [useAnalyze, setUseAnalyze] = useState(true);
  const [copied, setCopied] = useState(false);
  const { data, isLoading, isError, execute, reset } = useExplainAnalyze();

  // Execute when modal opens or analyze toggle changes
  const handleExecute = async () => {
    await execute({ query, analyze: useAnalyze });
  };

  // Handle modal open/close
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset();
      setCopied(false);
    }
    onOpenChange(newOpen);
  };

  // Copy plan to clipboard
  const handleCopy = async () => {
    if (data?.plan) {
      const planText = formatPlanNode(data.plan[0]?.Plan || data.plan);
      await navigator.clipboard.writeText(planText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Format the plan for display
  const formattedPlan = data?.plan
    ? formatPlanNode(data.plan[0]?.Plan || data.plan)
    : '';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Query Execution Plan
          </DialogTitle>
          <DialogDescription>
            View the PostgreSQL execution plan for this query
          </DialogDescription>
        </DialogHeader>

        {/* Query Display */}
        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          <div className="space-y-2">
            <label className="text-sm font-medium">Query</label>
            <pre className="text-xs font-mono bg-muted p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-all max-h-24">
              {query}
            </pre>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Button
                variant={useAnalyze ? 'default' : 'outline'}
                size="sm"
                onClick={() => setUseAnalyze(true)}
                disabled={isLoading}
              >
                EXPLAIN ANALYZE
              </Button>
              <Button
                variant={!useAnalyze ? 'default' : 'outline'}
                size="sm"
                onClick={() => setUseAnalyze(false)}
                disabled={isLoading}
              >
                EXPLAIN Only
              </Button>
              <span className="text-xs text-muted-foreground ml-2">
                {useAnalyze ? '(Executes query to show actual timing)' : '(Shows plan without executing)'}
              </span>
            </div>

            <Button
              onClick={handleExecute}
              disabled={isLoading}
              className="gap-2"
            >
              <Play className="h-4 w-4" />
              {isLoading ? 'Analyzing...' : 'Analyze'}
            </Button>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          )}

          {/* Error State */}
          {isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Analysis Failed</AlertTitle>
              <AlertDescription>
                {isError.message || 'Failed to analyze query. The query may be invalid or contain syntax errors.'}
              </AlertDescription>
            </Alert>
          )}

          {/* Results */}
          {data && !isLoading && (
            <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
              {/* Metrics Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {data.planning_time_ms !== undefined && (
                  <div className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      Planning Time
                    </div>
                    <div className="font-medium">{formatMs(data.planning_time_ms)}</div>
                  </div>
                )}
                {data.execution_time_ms !== undefined && (
                  <div className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      Execution Time
                    </div>
                    <div className="font-medium">{formatMs(data.execution_time_ms)}</div>
                  </div>
                )}
                {data.total_cost !== undefined && (
                  <div className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Calculator className="h-3 w-3" />
                      Total Cost
                    </div>
                    <div className="font-medium">{data.total_cost.toFixed(2)}</div>
                  </div>
                )}
                {data.actual_rows !== undefined && (
                  <div className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Rows3 className="h-3 w-3" />
                      Actual Rows
                    </div>
                    <div className="font-medium">
                      {data.actual_rows.toLocaleString()}
                      {data.estimated_rows !== undefined && (
                        <span className="text-xs text-muted-foreground ml-1">
                          (est: {data.estimated_rows.toLocaleString()})
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Plan Output */}
              <div className="flex-1 overflow-hidden flex flex-col">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Execution Plan</label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopy}
                    className="gap-2"
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
                <pre className="text-xs font-mono bg-muted p-4 rounded-md overflow-auto flex-1 whitespace-pre">
                  {formattedPlan || JSON.stringify(data.plan, null, 2)}
                </pre>
              </div>

              {/* Analysis Mode Badge */}
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Badge variant={data.analyze ? 'default' : 'secondary'}>
                  {data.analyze ? 'EXPLAIN ANALYZE' : 'EXPLAIN'}
                </Badge>
                <span>•</span>
                <span>Generated at {new Date(data.timestamp).toLocaleString()}</span>
              </div>
            </div>
          )}

          {/* Initial State - No data yet */}
          {!data && !isLoading && !isError && (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center space-y-2">
                <FileText className="h-12 w-12 mx-auto opacity-50" />
                <p>Click "Analyze" to generate the execution plan</p>
                <p className="text-xs">
                  {useAnalyze
                    ? 'EXPLAIN ANALYZE will execute the query to show actual timing'
                    : 'EXPLAIN will show the plan without executing the query'}
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
